import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import {
  FieldId,
  InputPartType,
} from "@redotech/redo-return-core-common/return-flow-answers";
import {
  InputContentType,
  InputValidation,
} from "@redotech/redo-return-core-common/return-flow-enums";
import type { InputState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  AI_VERIFICATION_TYPES,
  type AiVerificationType,
} from "@redotech/redo-return-core-common/return-flow/ai-verification-registry";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { ImageUpload } from "@redotech/redo-web/image-upload";
import { Text } from "@redotech/redo-web/text";
import { memo, useId, useMemo } from "react";
import { useFieldEditable } from "../flow-editor/flow-readonly-context";
import {
  customRegexErrorMessageFieldSpec,
  descriptionFieldSpec,
  promptFieldSpec,
} from "./translations/field-specs";
import { FlowFieldTranslations } from "./translations/flow-field-translations";
import { StepTranslationLanguages } from "./translations/step-translation-languages";
import {
  stepElementIdsOf,
  StepTranslationElement,
} from "./translations/step-translations";
import { TranslatableTextareaField } from "./translations/translatable-textarea-field";

const typeOptions: InputPartType[] = [
  InputPartType.IMAGES,
  InputPartType.TEXTAREA,
  InputPartType.VIDEOS,
  InputPartType.DATE,
];

function typeLabel(type: InputPartType, t: (key: string) => string): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (type) {
    case InputPartType.IMAGES:
      return t("Images");
    case InputPartType.VIDEOS:
      return t("Videos");
    case InputPartType.DATE:
      return t("Date");
    default:
      return t("Text");
  }
}

const contentTypeOptions: InputContentType[] = [
  InputContentType.ANY,
  InputContentType.NUMBERS,
  InputContentType.LETTERS,
];

function contentTypeLabel(
  type: InputContentType,
  t: (key: string) => string,
): string {
  switch (type) {
    case InputContentType.ANY:
      return t("Any");
    case InputContentType.NUMBERS:
      return t("Numbers only");
    case InputContentType.LETTERS:
      return t("Letters only");
  }
}

export const InputDetails = memo(function InputDetails({
  stepSelect,
  state,
  setState,
  flowType,
}: StepTypeDetailsProps<InputState>) {
  const { t } = useTranslation();
  const canEditReturnsSettings = useFieldEditable(
    useHasPermission(SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS),
  );
  const handlePrompt = useHandler((prompt: string) => {
    setState((state) => ({ ...state, prompt }));
  });
  const handleDescription = useHandler((description: string) => {
    setState((state) => ({ ...state, description }));
  });
  const handleType = useHandler((selectedType: InputPartType) => {
    setState((state) => ({ ...state, type: selectedType }));
  });
  const handleRequired = useHandler((value: boolean) => {
    setState((state) => ({
      ...state,
      validation: value ? InputValidation.REQUIRED : InputValidation.OPTIONAL,
    }));
  });
  const handleExampleImage = useHandler((exampleImage: URL | undefined) => {
    setState((state) => ({ ...state, exampleImage }));
  });
  const handleAiVerification = useHandler((value: boolean) => {
    const defaultType = AI_VERIFICATION_TYPES[0];
    setState((state) => ({
      ...state,
      aiVerification: value ? defaultType : undefined,
      aiVerificationStrictness: value ? 5 : undefined,
    }));
  });
  const handleAiVerificationType = useHandler((type: AiVerificationType) => {
    setState((state) => ({ ...state, aiVerification: type }));
  });
  const handleAiVerificationStrictness = useHandler((value: number) => {
    setState((state) => ({ ...state, aiVerificationStrictness: value }));
  });
  const handleMinLength = useHandler((value: string) => {
    const parsed = value === "" ? undefined : parseInt(value, 10);
    setState((state) => ({
      ...state,
      minLength: parsed !== undefined && !isNaN(parsed) ? parsed : undefined,
    }));
  });
  const handleMaxLength = useHandler((value: string) => {
    const parsed = value === "" ? undefined : parseInt(value, 10);
    setState((state) => ({
      ...state,
      maxLength: parsed !== undefined && !isNaN(parsed) ? parsed : undefined,
    }));
  });
  const handleContentType = useHandler((contentType: InputContentType) => {
    setState((state) => ({ ...state, contentType }));
  });
  const handleCustomRegex = useHandler((customRegex: string) => {
    setState((state) => ({ ...state, customRegex: customRegex || undefined }));
  });
  const handleCustomRegexErrorMessage = useHandler((msg: string) => {
    setState((state) => ({
      ...state,
      customRegexErrorMessage: msg || undefined,
    }));
  });
  const handleAdvancedValidation = useHandler((value: boolean) => {
    setState((state) => ({ ...state, useAdvancedValidation: value }));
  });
  const exampleImageId = useId();

  const regexError = useMemo(() => {
    if (!state.customRegex) {
      return undefined;
    }
    try {
      new RegExp(state.customRegex);
      return undefined;
    } catch {
      return t("Invalid regex pattern");
    }
  }, [state.customRegex, t]);

  const strictnessOptions = useMemo(
    () =>
      Array.from({ length: 10 }, (_, i) => i + 1).map((n) => ({
        type: "text" as const,
        id: String(n),
        text:
          n === 1
            ? t("{{count}} (least strict)", { count: n })
            : n === 10
              ? t("{{count}} (most strict)", { count: n })
              : String(n),
        value: n,
      })),
    [t],
  );

  const selectedStrictnessItem = useMemo(() => {
    const val = state.aiVerificationStrictness ?? 5;
    return strictnessOptions.find((o) => o.value === val);
  }, [state.aiVerificationStrictness, strictnessOptions]);

  const verificationTypeOptions = useMemo(
    () =>
      AI_VERIFICATION_TYPES.map((key) => ({
        type: "text" as const,
        id: key,
        text: key
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" "),
        value: key,
      })),
    [],
  );

  const selectedVerificationType = useMemo(() => {
    if (!state.aiVerification) {
      return undefined;
    }
    return verificationTypeOptions.find(
      (o) => o.value === state.aiVerification,
    );
  }, [state.aiVerification, verificationTypeOptions]);

  const typeListItems = useMemo<
    { type: "text"; id: InputPartType; text: string; value: InputPartType }[]
  >(
    () =>
      typeOptions.map((type) => ({
        type: "text" as const,
        id: type,
        text: typeLabel(type, t),
        value: type,
      })),
    [t],
  );

  const selectedTypeItem = useMemo<
    | { type: "text"; id: InputPartType; text: string; value: InputPartType }
    | undefined
  >(
    () =>
      state.type
        ? {
            type: "text" as const,
            id: state.type,
            text: typeLabel(state.type, t),
            value: state.type,
          }
        : undefined,
    [state.type, t],
  );

  const contentTypeListItems = useMemo(
    () =>
      contentTypeOptions.map((ct) => ({
        type: "text" as const,
        id: ct,
        text: contentTypeLabel(ct, t),
        value: ct,
      })),
    [t],
  );

  const selectedContentTypeItem = useMemo(() => {
    const ct = state.contentType ?? InputContentType.ANY;
    return {
      type: "text" as const,
      id: ct,
      text: contentTypeLabel(ct, t),
      value: ct,
    };
  }, [state.contentType, t]);

  const regexErrorInputVisible = !!(
    state.useAdvancedValidation && state.customRegex
  );
  const translationElements = useMemo<StepTranslationElement[]>(() => {
    const elements: StepTranslationElement[] = [
      { elementId: state.partUUID, field: promptFieldSpec(state.prompt) },
      {
        elementId: state.partUUID,
        field: descriptionFieldSpec(state.description),
      },
    ];
    if (regexErrorInputVisible) {
      elements.push({
        elementId: state.partUUID,
        field: customRegexErrorMessageFieldSpec(
          state.customRegexErrorMessage ?? "",
        ),
      });
    }
    return elements;
  }, [
    state.partUUID,
    state.prompt,
    state.description,
    regexErrorInputVisible,
    state.customRegexErrorMessage,
  ]);
  const stepElementIds = useMemo(
    () => stepElementIdsOf(translationElements),
    [translationElements],
  );

  return (
    <ClientUploader>
      <RedoToggle
        disabled={!canEditReturnsSettings}
        label={t("Required")}
        setValue={handleRequired}
        value={state.validation === InputValidation.REQUIRED}
      />
      <StepTranslationLanguages
        canEdit={canEditReturnsSettings}
        elements={translationElements}
        flowType={flowType}
      />
      <TranslatableTextareaField
        canEdit={canEditReturnsSettings}
        elementId={state.partUUID}
        errorText={state.attempted && !state.prompt ? t("Required") : undefined}
        field={promptFieldSpec(state.prompt)}
        flowType={flowType}
        placeholder={t("Enter prompt...")}
        setValue={handlePrompt}
        stepElementIds={stepElementIds}
      />
      <TranslatableTextareaField
        canEdit={canEditReturnsSettings}
        elementId={state.partUUID}
        field={descriptionFieldSpec(state.description)}
        flowType={flowType}
        placeholder={t("Enter description...")}
        setValue={handleDescription}
        stepElementIds={stepElementIds}
      />
      {state.fieldId !== FieldId.WARRANTIES_ORDER_DATE && (
        <RedoSingleSelectDropdownInput
          disabled={!canEditReturnsSettings}
          flexGrow={false}
          label={t("Answer type")}
          options={typeListItems}
          optionSelected={({ item }) => handleType(item.value)}
          selectedItem={selectedTypeItem}
          size="xs"
        />
      )}
      {state.type === InputPartType.IMAGES && (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Image")}
            </Text>
            <ImageUpload
              disabled={!canEditReturnsSettings}
              id={exampleImageId}
              onChange={handleExampleImage}
              value={state.exampleImage}
            />
            <Text fontSize="xs" fontWeight="regular" textColor="secondary">
              {t(
                "Optional. Upload an image to show the customer an example of what you're looking for.",
              )}
            </Text>
          </Flex>
          <Divider />
          <RedoToggle
            disabled={!canEditReturnsSettings}
            label={t("AI verification")}
            setValue={handleAiVerification}
            value={!!state.aiVerification}
          />
          {state.aiVerification && (
            <>
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label={t("Verification type")}
                options={verificationTypeOptions}
                optionSelected={({ item }) =>
                  handleAiVerificationType(item.value)
                }
                selectedItem={selectedVerificationType}
                size="xs"
              />
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label={t("Strictness")}
                options={strictnessOptions}
                optionSelected={({ item }) =>
                  handleAiVerificationStrictness(item.value)
                }
                selectedItem={selectedStrictnessItem}
                size="xs"
              />
            </>
          )}
        </>
      )}
      {state.type === InputPartType.TEXTAREA && (
        <>
          <Divider />
          <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
            {t("Text validation")}
          </Text>
          <RedoToggle
            disabled={!canEditReturnsSettings}
            label={t("Advanced validation")}
            setValue={handleAdvancedValidation}
            value={!!state.useAdvancedValidation}
          />
          {!state.useAdvancedValidation && (
            <>
              <Flex dir="row" gap="md">
                <Flex dir="column" gap="xs" grow={1}>
                  <RedoTextInput
                    inputTypeHint="number"
                    label={t("Min characters")}
                    min={0}
                    placeholder={t("No minimum")}
                    setValue={handleMinLength}
                    size="sm"
                    state={!canEditReturnsSettings ? "disabled" : "default"}
                    value={state.minLength?.toString() ?? ""}
                  />
                </Flex>
                <Flex dir="column" gap="xs" grow={1}>
                  <RedoTextInput
                    inputTypeHint="number"
                    label={t("Max characters")}
                    min={1}
                    placeholder={t("No maximum")}
                    setValue={handleMaxLength}
                    size="sm"
                    state={!canEditReturnsSettings ? "disabled" : "default"}
                    value={state.maxLength?.toString() ?? ""}
                  />
                </Flex>
              </Flex>
              <RedoSingleSelectDropdownInput
                disabled={!canEditReturnsSettings}
                flexGrow={false}
                label={t("Content type")}
                options={contentTypeListItems}
                optionSelected={({ item }) => handleContentType(item.value)}
                selectedItem={selectedContentTypeItem}
                size="xs"
              />
            </>
          )}
          {state.useAdvancedValidation && (
            <>
              <RedoTextInput
                errorText={regexError}
                label={t("Custom regex pattern")}
                placeholder={t("e.g. ^[A-Z]{2}\\d{4}$")}
                setValue={handleCustomRegex}
                size="sm"
                state={!canEditReturnsSettings ? "disabled" : "default"}
                value={state.customRegex ?? ""}
              />
              {state.customRegex && (
                <>
                  <RedoTextInput
                    label={t("Validation error message")}
                    placeholder={t("Input does not match required format")}
                    setValue={handleCustomRegexErrorMessage}
                    size="sm"
                    state={!canEditReturnsSettings ? "disabled" : "default"}
                    value={state.customRegexErrorMessage ?? ""}
                  />
                  <FlowFieldTranslations
                    canEdit={canEditReturnsSettings}
                    elementId={state.partUUID}
                    field={customRegexErrorMessageFieldSpec(
                      state.customRegexErrorMessage ?? "",
                    )}
                    flowType={flowType}
                    stepElementIds={stepElementIds}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      <Divider />
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next")}
        </Text>
        {stepSelect({
          value: state.next,
          valueChange: canEditReturnsSettings
            ? (next) => setState({ ...state, next })
            : () => {},
        })}
      </Flex>
    </ClientUploader>
  );
});
